import { useEffect, useMemo, useRef, useState } from 'react'

import type { SessionResponse } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { timeAgo } from '@/lib/format'
import { Modal } from '@/ui/Modal'

interface Props {
  open: boolean
  onClose: () => void
  sessions: SessionResponse[]
  activeId: string | null
  onPickSession: (s: SessionResponse) => void
  onNewSession: () => void
}

type Item =
  | { kind: 'new' }
  | { kind: 'session'; session: SessionResponse }

function sessionHeadline(s: SessionResponse): string {
  if (s.title && s.title.trim()) return s.title
  return 'Untitled'
}

export function CommandBar({ open, onClose, sessions, activeId, onPickSession, onNewSession }: Props) {
  const { user } = useAuth()
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  const items = useMemo<Item[]>(() => {
    const q = query.trim().toLowerCase()
    const filtered = q
      ? sessions.filter(
          (s) =>
            s.title.toLowerCase().includes(q) ||
            s.session_id.toLowerCase().includes(q) ||
            s.owner_email.toLowerCase().includes(q),
        )
      : sessions
    return [{ kind: 'new' as const }, ...filtered.map((s) => ({ kind: 'session' as const, session: s }))]
  }, [sessions, query])

  useEffect(() => {
    setSelectedIndex(0)
  }, [query, open])

  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((i) => (i + 1) % Math.max(1, items.length))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((i) => (i - 1 + items.length) % Math.max(1, items.length))
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        const item = items[selectedIndex]
        if (!item) return
        runItem(item)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, items, selectedIndex, onClose])

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${selectedIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  function runItem(item: Item) {
    if (item.kind === 'new') {
      onNewSession()
    } else {
      onPickSession(item.session)
    }
    onClose()
  }

  if (!open) return null

  return (
    <Modal open={open} onClose={onClose} size="xl" className="overflow-hidden rounded-2xl">
      <div style={{ boxShadow: 'var(--shadow-elevated)' }}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-haze/40">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-mute">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search consultations…"
            className="flex-1 bg-transparent text-base text-cream placeholder-mute/50 outline-none"
            style={{ caretColor: 'var(--color-ember)' }}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:inline-block font-mono text-[11px] text-mute border border-haze/50 px-2 py-0.5 rounded-md">
            esc
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-1">
          {items.map((item, index) => {
            const isSelected = index === selectedIndex
            if (item.kind === 'new') {
              return (
                <button
                  key="new"
                  data-index={index}
                  onClick={() => runItem(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`w-full flex items-center gap-3 px-5 py-3.5 text-left transition-all duration-200 ${
                    isSelected ? 'bg-ember/8' : ''
                  }`}
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-ember/10 text-ember text-sm">+</span>
                  <span className="font-display italic text-base text-cream">New consultation</span>
                  <span className="ml-auto text-xs text-mute">Start fresh</span>
                </button>
              )
            }

            const s = item.session
            const isActive = s.session_id === activeId
            return (
              <button
                key={s.session_id}
                data-index={index}
                onClick={() => runItem(item)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`w-full flex items-center gap-3 px-5 py-3.5 text-left transition-all duration-200 ${
                  isSelected ? 'bg-ember/8' : ''
                }`}
              >
                <span className="font-display text-base text-cream truncate flex-1">
                  {sessionHeadline(s)}
                </span>
                {user?.role === 'admin' && s.owner_email && (
                  <span className="text-xs text-mute/70 truncate hidden md:inline">
                    {s.owner_email.split('@')[0]}
                  </span>
                )}
                <span className="text-xs text-mute shrink-0">{timeAgo(s.last_used_at)}</span>
                {isActive && <span className="w-1.5 h-1.5 rounded-full bg-ember shrink-0" />}
              </button>
            )
          })}

          {items.length === 1 && query.trim() && (
            <div className="px-5 py-6 text-center text-sm text-mute italic font-display">
              No consultations match "{query}"
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-2.5 border-t border-haze/40 bg-ink/40">
          <div className="flex items-center gap-4 text-xs text-mute">
            <span className="flex items-center gap-1.5">
              <kbd className="font-mono text-[10px] border border-haze/50 px-1 rounded-md">↑↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="font-mono text-[10px] border border-haze/50 px-1 rounded-md">↵</kbd>
              select
            </span>
          </div>
          <span className="text-xs text-mute/70">
            {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}
          </span>
        </div>
      </div>
    </Modal>
  )
}
